import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import AccountsAPI from "../../services/AccountsAPI";
import Page from "../../components/Page";
import backgroundImage from "../../images/Sign-Up-Background.jpg";
import { AppButton, AppTextField } from "../../components/ui";

const PASSWORD_RULES_MESSAGE =
  "Password must be at least 8 characters and include uppercase, lowercase, and a number.";

const getApiErrorMessage = (err, statusMessages, fallbackMessage) => {
  const backendMessage = err?.response?.data?.message;
  const status = err?.response?.status;

  if (typeof backendMessage === "string" && backendMessage.trim()) {
    return backendMessage;
  }

  if (statusMessages?.[status]) {
    return statusMessages[status];
  }

  return fallbackMessage;
};

const isPasswordValid = (password) =>
  /[A-Z]/.test(password) &&
  /[a-z]/.test(password) &&
  /\d/.test(password) &&
  password.length >= 8;

const isEmailValid = (email) => /\S+@\S+\.\S+/.test(email);

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    organization: "",
    password: "",
    confirmPassword: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmationCode, setConfirmationCode] = useState("");
  const [confirmationCodeError, setConfirmationCodeError] = useState("");
  const [step, setStep] = useState("details");
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [isSubmittingDetails, setIsSubmittingDetails] = useState(false);
  const [isConfirmingCode, setIsConfirmingCode] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);

  useEffect(() => {
    if (step !== "success") {
      return undefined;
    }

    const timer = setTimeout(() => {
      navigate("/login", {
        replace: true,
        state: {
          accountMessage: "Account confirmed, please sign in.",
        },
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [navigate, step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "password" && fieldErrors.confirmPassword) {
      setFieldErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }

    setFormError("");
  };

  const validateSignUpForm = () => {
    const errors = {};
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const email = formData.email.trim();
    const organization = formData.organization.trim();

    if (!firstName) {
      errors.firstName = "First name is required.";
    }
    if (!lastName) {
      errors.lastName = "Last name is required.";
    }
    if (!email) {
      errors.email = "Email is required.";
    } else if (!isEmailValid(email)) {
      errors.email = "Enter a valid email address.";
    }
    if (!organization) {
      errors.organization = "Organization is required.";
    }
    if (!formData.password) {
      errors.password = "Password is required.";
    } else if (!isPasswordValid(formData.password)) {
      errors.password = PASSWORD_RULES_MESSAGE;
    }
    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = "Passwords do not match.";
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!validateSignUpForm()) {
      return;
    }

    const payload = {
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      organization: formData.organization.trim(),
      password: formData.password,
    };

    try {
      setFormError("");
      setFormSuccess("");
      setIsSubmittingDetails(true);

      await AccountsAPI.post("/sign-up", payload, { requiresAuth: false });

      setFormData((prev) => ({
        ...prev,
        firstName: payload.firstName,
        middleName: payload.middleName,
        lastName: payload.lastName,
        email: payload.email,
        organization: payload.organization,
      }));
      setStep("confirm");
      setConfirmationCode("");
      setConfirmationCodeError("");
      setFormSuccess(`We sent a confirmation code to ${payload.email}.`);
    } catch (err) {
      setFormError(
        getApiErrorMessage(
          err,
          {
            400: "Please review your details and password requirements, then try again.",
            409: "An account with this email already exists. Please sign in or reset your password.",
            429: "Too many signup attempts. Please wait a few minutes and try again.",
          },
          "Something went wrong while creating your account. Please try again."
        )
      );
    } finally {
      setIsSubmittingDetails(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    const normalizedCode = confirmationCode.trim();

    if (!normalizedCode) {
      setConfirmationCodeError("Confirmation code is required.");
      return;
    }

    try {
      setFormError("");
      setFormSuccess("");
      setIsConfirmingCode(true);

      await AccountsAPI.post(
        "/sign-up/confirm",
        {
          email: formData.email,
          confirmationCode: normalizedCode,
        },
        { requiresAuth: false }
      );

      setConfirmationCodeError("");
      setFormSuccess("Account confirmed. Redirecting to sign in...");
      setStep("success");
    } catch (err) {
      setFormError(
        getApiErrorMessage(
          err,
          {
            400: "That confirmation code is invalid or expired. Please try again or request a new code.",
            429: "Too many confirmation attempts. Please wait a few minutes and try again.",
          },
          "Unable to confirm your account right now. Please try again."
        )
      );
    } finally {
      setIsConfirmingCode(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setFormError("");
      setFormSuccess("");
      setIsResendingCode(true);

      await AccountsAPI.post(
        "/sign-up/resend-code",
        {
          email: formData.email,
        },
        { requiresAuth: false }
      );

      setFormSuccess(`A new confirmation code was sent to ${formData.email}.`);
    } catch (err) {
      setFormError(
        getApiErrorMessage(
          err,
          {
            400: "We couldn't resend a code for this account. Please restart signup if needed.",
            429: "Too many resend requests. Please wait a few minutes before trying again.",
          },
          "Unable to resend confirmation code right now. Please try again."
        )
      );
    } finally {
      setIsResendingCode(false);
    }
  };

  const features = [
    "Full access to 10k Science Viewer with NGSS-aligned experiences on laptops and desktops",
    "Insights Dashboard with AI-assisted student analytics",
    "Join Codes for your students",
    "AI audio guides for students",
  ];

  return (
    <Page>
      <Box display="flex" height="100vh">
        {/* Left Side: Information Section */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          textAlign="center"
          alignItems="center"
          justifyContent="center"
          padding={20}
          sx={{
            position: "relative", // Enable pseudo-element positioning
            backgroundImage: `url(${backgroundImage})`, // Use the imported image
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            "::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "var(--color-auth-overlay)",
              zIndex: 1,
            },
            zIndex: 2,
          }}
        >
          {/* Inner Box for content */}
          <Box
            sx={{
              position: "relative", // To ensure it's above the overlay
              zIndex: 2,
              backgroundColor: "var(--color-bg-card)",
              borderRadius: "20px", // Rounded corners
              padding: "80px 60px", // Add padding
              maxWidth: "800px", // Limit content width
              width: "100%",
              boxShadow: "var(--shadow-card-hover)",
              textAlign: "center", // Center-align text
            }}
          >
            <Typography variant="h1" component="h1" gutterBottom>
              See how 10k Science can be used in your classroom
            </Typography>
            <Typography
       
              variant="body1"
              color="textSecondary"
              gutterBottom
            >
              <strong>Your trial includes:</strong>
            </Typography>
            <List>
              {features.map((feature, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        {/* Right Side: Sign-Up Form */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          padding={4}
        >
          <Box width="100%" maxWidth={400} p={4} bgcolor="var(--color-bg-card)">
            {step === "details" ? (
              <form onSubmit={handleSignUp} noValidate>
                <Box marginBottom={2}>
                  <Typography
                    variant="h2"
                    component="h1"
                    align="center"
                    gutterBottom
                  >
                    Start your journey with a 7-day free trial.
                  </Typography>
                  <Typography variant="body2" align="center">
                    No credit card required.
                  </Typography>
                </Box>

                {formError && (
                  <Box marginBottom={2}>
                    <Alert severity="error">{formError}</Alert>
                  </Box>
                )}
                {formSuccess && (
                  <Box marginBottom={2}>
                    <Alert severity="success">{formSuccess}</Alert>
                  </Box>
                )}

                <AppTextField
                  label="First Name"
                  fullWidth
                  margin="normal"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={Boolean(fieldErrors.firstName)}
                  helperText={fieldErrors.firstName}
                  required
                />
                <AppTextField
                  label="Middle Name"
                  fullWidth
                  margin="normal"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                />
                <AppTextField
                  label="Last Name"
                  fullWidth
                  margin="normal"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={Boolean(fieldErrors.lastName)}
                  helperText={fieldErrors.lastName}
                  required
                />
                <AppTextField
                  label="Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email}
                  required
                />
                <AppTextField
                  label="Organization"
                  fullWidth
                  margin="normal"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  error={Boolean(fieldErrors.organization)}
                  helperText={fieldErrors.organization}
                  required
                />
                <AppTextField
                  label="Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={Boolean(fieldErrors.password)}
                  helperText={fieldErrors.password}
                  required
                />
                <AppTextField
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  margin="normal"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={Boolean(fieldErrors.confirmPassword)}
                  helperText={fieldErrors.confirmPassword}
                  required
                />

                <Box display="flex" justifyContent="center" marginTop={2}>
                  <AppButton
                    type="submit"
                    kind="primary"
                    fullWidth
                    loading={isSubmittingDetails}
                    loadingLabel="Creating account..."
                    disabled={isSubmittingDetails}
                  >
                    Start Free Trial
                  </AppButton>
                </Box>
              </form>
            ) : (
              <form onSubmit={handleConfirmSignUp} noValidate>
                <Box marginBottom={2}>
                  <Typography
                    variant="h2"
                    component="h1"
                    align="center"
                    gutterBottom
                  >
                    Confirm your account
                  </Typography>
                  <Typography variant="body2" align="center">
                    Enter the confirmation code sent to {formData.email}.
                  </Typography>
                </Box>

                {formError && (
                  <Box marginBottom={2}>
                    <Alert severity="error">{formError}</Alert>
                  </Box>
                )}
                {formSuccess && (
                  <Box marginBottom={2}>
                    <Alert severity="success">{formSuccess}</Alert>
                  </Box>
                )}

                <AppTextField
                  label="Confirmation Code"
                  fullWidth
                  margin="normal"
                  name="confirmationCode"
                  value={confirmationCode}
                  onChange={(e) => {
                    setConfirmationCode(e.target.value);
                    if (confirmationCodeError) {
                      setConfirmationCodeError("");
                    }
                    setFormError("");
                  }}
                  error={Boolean(confirmationCodeError)}
                  helperText={confirmationCodeError}
                  required
                  disabled={step === "success"}
                />

                <Box display="flex" flexDirection="column" gap={1.5} marginTop={2}>
                  <AppButton
                    type="submit"
                    kind="primary"
                    fullWidth
                    loading={isConfirmingCode}
                    loadingLabel="Confirming..."
                    disabled={isConfirmingCode || isResendingCode || step === "success"}
                  >
                    Confirm Account
                  </AppButton>
                  <AppButton
                    type="button"
                    kind="ghost"
                    fullWidth
                    loading={isResendingCode}
                    loadingLabel="Resending..."
                    onClick={handleResendCode}
                    disabled={isConfirmingCode || isResendingCode || step === "success"}
                  >
                    Resend code
                  </AppButton>
                </Box>
              </form>
            )}
          </Box>
        </Box>
      </Box>
    </Page>
  );
};

export default SignUp;
